import { Alert, Button, Progress } from "reactstrap";
import {
  calculateProgressInPercent,
  CurrentState,
  OfflineUpdateSummary,
} from "./OfflineUpdateSummary";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationCircle,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { CyOffline } from "./CyOffline";
import { useTranslation } from "react-i18next";

interface Props {
  offlineUpdateSummary: OfflineUpdateSummary;
  resetProgress: () => void;
}

export default function DownloadIndicator({
  offlineUpdateSummary,
  resetProgress,
}: Props) {
  const { t } = useTranslation();
  const currentState = offlineUpdateSummary.currentState;

  return (
    <div className="mt-2">
      {currentState === CurrentState.NOT_RUNNING && <></>}
      {currentState === CurrentState.REFRESHING_TOC && (
        <Progress animated value="100" style={{ height: "71px" }}>
          {t("offline.download.refreshingToc")}
        </Progress>
      )}
      {currentState === CurrentState.CHECKING_CURRENT_DATA_IN_CACHE && (
        <Progress animated value="100" style={{ height: "71px" }}>
          {t("offline.download.checkingCache")}
        </Progress>
      )}
      {currentState === CurrentState.CALCULATE_DELTA && (
        <Progress animated value="100" style={{ height: "71px" }}>
          {t("offline.download.calculateDelta")}
        </Progress>
      )}
      {currentState === CurrentState.DELETE_OLD && (
        <Progress animated={true} value="100" style={{ height: "71px" }}>
          {t("offline.download.deleteOld")}
        </Progress>
      )}
      {currentState === CurrentState.DOWNLOAD && (
        <Progress
          animated={currentState === CurrentState.DOWNLOAD}
          value={calculateProgressInPercent(offlineUpdateSummary)}
          style={{ height: "71px" }}
        >
          {t("offline.download.downloading", {
            percent: calculateProgressInPercent(offlineUpdateSummary),
          })}
        </Progress>
      )}
      {currentState === CurrentState.FINISHED && (
        <Progress animated={false} value="100" style={{ height: "71px" }}>
          <Button
            color="primary"
            onClick={resetProgress}
            data-cy={CyOffline.ConfirmDownloadFinishedButton}
          >
            {t("offline.download.finished")}
          </Button>
        </Progress>
      )}
      {currentState === CurrentState.FINISHED_WITH_ERRORS && (
        <Alert color="warning" fade={false}>
          <h2 className={"light-header"}>
            <FontAwesomeIcon icon={faExclamationTriangle} />{" "}
            {t("offline.download.finishedWithErrorsTitle")}
          </h2>
          {t("offline.download.finishedWithErrorsMessage")}
          <br />
          {t("offline.download.supportMessage")}
          <br />
          <br />
          <Button color="primary" onClick={resetProgress}>
            {t("offline.download.close")}
          </Button>
        </Alert>
      )}
      {currentState === CurrentState.ABORTED_WITH_ERROR && (
        <Alert color="danger" fade={false}>
          <h2 className={"light-header"}>
            <FontAwesomeIcon icon={faExclamationCircle} />{" "}
            {t("offline.download.abortedTitle")}
          </h2>
          {t("offline.download.abortedMessage")}
          <br />
          {t("offline.download.supportMessage")}
          <br />
          <br />
          <Button color="primary" onClick={resetProgress}>
            {t("offline.download.close")}
          </Button>
        </Alert>
      )}
    </div>
  );
}
